import { CastDebugLogger } from 'chromecast-caf-receiver/cast.debug';
import { LoggerLevel } from 'chromecast-caf-receiver/cast.framework';
import { Config } from './config';

/**
 * Centralized logger for the Cast receiver.
 *
 * Ported from `legacy/js/logs.js` to TypeScript. Replaces the legacy `Logs`
 * class with structured log methods (`info`, `warn`, `error`, `debug`) and
 * integrates with the Cast SDK's `CastDebugLogger` for remote log viewing
 * via the Command-and-Control (CaC) tool.
 *
 * Key improvements over the legacy implementation:
 * - Typed log levels instead of string matching for "error"/"ERROR"
 * - Native `Intl.DateTimeFormat` replaces the `moment.js` dependency
 * - `CastDebugLogger` integration for remote debugging
 * - `DEBUG_MODE` automatically enables verbose logs on `dev-*` hostnames
 * - DOM overlay preserved for on-device visual debugging
 */

/** Default custom tag used in `CastDebugLogger` calls. */
const DEFAULT_TAG = 'BPReceiver';

/** Formatter for human-readable timestamps (ISO-like, no external deps). */
const timestampFormatter = new Intl.DateTimeFormat('sv-SE', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  timeZone: 'America/Sao_Paulo',
});

/**
 * Returns a formatted timestamp string.
 * Example output: `2026-03-04 14:30:15.123`
 */
function formatTimestamp(): string {
  const now = Date.now();
  const base = timestampFormatter.format(now);
  const ms = String(now % 1000).padStart(3, '0');
  return `${base}.${ms}`;
}

/**
 * Serializes an arbitrary value for log output.
 * Objects are JSON-stringified; primitives are coerced to string.
 */
function serialize(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (value instanceof Error) {
    return value.stack ?? `${value.name}: ${value.message}`;
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/**
 * Centralized Logger — static class providing structured logging with
 * `CastDebugLogger` integration and on-device DOM overlay.
 *
 * Usage:
 * ```ts
 * import { Logger } from './logger';
 *
 * Logger.init();
 * Logger.info('LOAD', 'Received LOAD request', { mediaId: '123' });
 * Logger.error('DRM', 'License request failed', error);
 * ```
 */
export class Logger {
  private static castDebugLogger: CastDebugLogger | null = null;
  private static debugMode: boolean = false;
  private static initialized = false;

  /**
   * Initializes the logger.
   *
   * - Detects `DEBUG_MODE` from `Config`
   * - Sets up `CastDebugLogger` with appropriate log levels
   * - Enables the DOM overlay when in debug mode
   *
   * Should be called once during app startup, after `Config` is available.
   */
  static init(): void {
    if (Logger.initialized) return;

    Logger.debugMode = Config.DEBUG_MODE;

    try {
      Logger.castDebugLogger = CastDebugLogger.getInstance();
      Logger.castDebugLogger.setEnabled(true);

      Logger.castDebugLogger.loggerLevelByTags = {
        [DEFAULT_TAG]: Logger.debugMode
          ? LoggerLevel.DEBUG
          : LoggerLevel.INFO,
      };

      if (Logger.debugMode) {
        Logger.castDebugLogger.showDebugLogs(true);
      }
    } catch {
      // CastDebugLogger may not be available in all environments
      Logger.castDebugLogger = null;
    }

    // DOM overlay starts hidden; toggled explicitly via debug-channel
    Logger.initialized = true;
  }

  /**
   * Logs an informational message.
   *
   * @param tag  - A short label identifying the source module (e.g. `'LOAD'`, `'DRM'`)
   * @param message - Human-readable log message
   * @param data - Optional structured data to include
   */
  static info(tag: string, message: string, data?: unknown): void {
    Logger.log('INFO', tag, message, data);
  }

  /**
   * Logs a warning message.
   *
   * @param tag  - A short label identifying the source module
   * @param message - Human-readable log message
   * @param data - Optional structured data to include
   */
  static warn(tag: string, message: string, data?: unknown): void {
    Logger.log('WARN', tag, message, data);
  }

  /**
   * Logs an error message.
   *
   * @param tag  - A short label identifying the source module
   * @param message - Human-readable log message
   * @param data - Optional error or structured data to include
   */
  static error(tag: string, message: string, data?: unknown): void {
    Logger.log('ERROR', tag, message, data);
  }

  /**
   * Logs a debug message. Only outputs when `DEBUG_MODE` is active.
   *
   * @param tag  - A short label identifying the source module
   * @param message - Human-readable log message
   * @param data - Optional structured data to include
   */
  static debug(tag: string, message: string, data?: unknown): void {
    if (!Logger.debugMode) return;
    Logger.log('DEBUG', tag, message, data);
  }

  /**
   * Toggles the on-device DOM debug overlay visibility.
   * Called by the debug channel when the sender requests it.
   */
  static toggleDOMOverlay(): void {
    const el = document.getElementById('logger');
    if (!el) return;
    const visible = el.style.display !== 'block';
    el.style.display = visible ? 'block' : 'none';
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Core log method. Routes output to:
   * 1. `CastDebugLogger` (for remote CaC tool viewing)
   * 2. Browser console (`console.log` / `console.error`)
   * 3. DOM overlay element (for on-device visual debugging)
   */
  private static log(
    level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR',
    tag: string,
    message: string,
    data?: unknown,
  ): void {
    const timestamp = formatTimestamp();
    const serialized = data !== undefined ? serialize(data) : '';
    const formatted = serialized
      ? `${timestamp} [${level}] ${tag}: ${message} — ${serialized}`
      : `${timestamp} [${level}] ${tag}: ${message}`;

    // 1. CastDebugLogger
    Logger.sendToCastDebugLogger(level, tag, message, data);

    // 2. Console
    Logger.sendToConsole(level, tag, message, data);

    // 3. DOM overlay
    Logger.appendToDOMOverlay(formatted, level);
  }

  /**
   * Routes the log to the Cast SDK's `CastDebugLogger` using the
   * appropriate severity method.
   */
  private static sendToCastDebugLogger(
    level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR',
    tag: string,
    message: string,
    data?: unknown,
  ): void {
    if (!Logger.castDebugLogger) return;

    const fullTag = `${DEFAULT_TAG}:${tag}`;
    const payload = data !== undefined ? `${message} ${serialize(data)}` : message;

    switch (level) {
      case 'DEBUG':
        Logger.castDebugLogger.debug(fullTag, payload);
        break;
      case 'INFO':
        Logger.castDebugLogger.info(fullTag, payload);
        break;
      case 'WARN':
        Logger.castDebugLogger.warn(fullTag, payload);
        break;
      case 'ERROR':
        Logger.castDebugLogger.error(fullTag, payload);
        break;
    }
  }

  /**
   * Routes the log to the browser console with appropriate severity.
   */
  private static sendToConsole(
    level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR',
    tag: string,
    message: string,
    data?: unknown,
  ): void {
    const prefix = `[${tag}]`;
    const args: unknown[] = data !== undefined ? [prefix, message, data] : [prefix, message];

    switch (level) {
      case 'DEBUG':
        console.debug(...args);
        break;
      case 'INFO':
        console.log(...args);
        break;
      case 'WARN':
        console.warn(...args);
        break;
      case 'ERROR':
        console.error(...args);
        break;
    }
  }

  // ---------------------------------------------------------------------------
  // DOM overlay (preserves legacy on-device visual debugging)
  // ---------------------------------------------------------------------------

  /**
   * Shows or hides the `#logger` DOM element used for on-device visual debugging.
   * Mirrors the legacy `Logs.enable()` behavior.
   */
  private static enableDOMOverlay(visible: boolean): void {
    const el = document.getElementById('logger');
    if (el) {
      el.style.display = visible ? 'block' : 'none';
    }
  }

  /** Max lines kept in the on-screen debug overlay. */
  private static readonly MAX_OVERLAY_LINES = 12;

  /**
   * Appends a formatted log line to the `#logger` DOM element.
   * Keeps only the last {@link MAX_OVERLAY_LINES} entries for readability.
   */
  private static appendToDOMOverlay(text: string, level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR'): void {
    const logger = document.getElementById('logger');
    if (!logger) return;

    const node = document.createElement('p');
    node.textContent = text;

    if (level === 'ERROR') {
      node.className = 'log-error';
    } else if (level === 'WARN') {
      node.className = 'log-warn';
    }

    logger.appendChild(node);

    // Trim old entries to keep the overlay minimal
    while (logger.childElementCount > Logger.MAX_OVERLAY_LINES) {
      logger.firstElementChild?.remove();
    }

    node.scrollIntoView();
  }
}
